import { computed } from 'vue';
import { useAccount } from 'dashboard/composables/useAccount';

export const DEFAULT_ARROWS_AI_PROVIDER = 'deepseek';
export const DEFAULT_ARROWS_AI_ASSISTANT_LIMIT = 0;
export const ARROWS_AI_PROVIDERS = ['openai', 'deepseek'];
export const MIN_ARROWS_AI_ASSISTANT_LIMIT = 0;
export const MAX_ARROWS_AI_ASSISTANT_LIMIT = 20;
export const DEFAULT_ARROWS_AI_INSTRUCTIONS_LIMIT = 50000;
export const MIN_ARROWS_AI_INSTRUCTIONS_LIMIT = 1000;
export const MAX_ARROWS_AI_INSTRUCTIONS_LIMIT = 500000;
export const ARROWS_AI_CHANNEL_TYPES = [
  'whatsapp',
  'instagram',
  'facebook',
  'email',
  'web_widget',
  'telegram',
];
export const DEFAULT_ARROWS_AI_CHANNEL_TYPES = ['whatsapp'];
export const DEFAULT_ARROWS_AI_INBOXES_PER_ASSISTANT = 1;
export const MIN_ARROWS_AI_INBOXES_PER_ASSISTANT = 1;
export const MAX_ARROWS_AI_INBOXES_PER_ASSISTANT = 20;

const CHANNEL_KEY_BY_TYPE = {
  'Channel::Whatsapp': 'whatsapp',
  'Channel::Instagram': 'instagram',
  'Channel::FacebookPage': 'facebook',
  'Channel::Email': 'email',
  'Channel::WebWidget': 'web_widget',
  'Channel::Telegram': 'telegram',
  'Channel::Api': 'api',
  'Channel::Sms': 'sms',
};

export const arrowsAiChannelKey = inbox => {
  const type = inbox?.channel_type || inbox?.channelType || '';
  if (type === 'Channel::TwilioSms') {
    return inbox?.medium === 'whatsapp' ? 'whatsapp' : 'sms';
  }

  return CHANNEL_KEY_BY_TYPE[type] || '';
};

const allowedChannelTypesFrom = value => {
  const selected = (Array.isArray(value) ? value : []).filter(type =>
    ARROWS_AI_CHANNEL_TYPES.includes(type)
  );

  return selected.length ? selected : DEFAULT_ARROWS_AI_CHANNEL_TYPES;
};

const parseFiniteNumber = value => {
  if (value === null || value === undefined || value === '') return null;

  const parsed = Number(value);
  return Number.isFinite(parsed) ? Math.round(parsed) : null;
};

const clampLimit = value => {
  const parsed = parseFiniteNumber(value);
  if (parsed === null) return DEFAULT_ARROWS_AI_ASSISTANT_LIMIT;

  return Math.min(
    MAX_ARROWS_AI_ASSISTANT_LIMIT,
    Math.max(MIN_ARROWS_AI_ASSISTANT_LIMIT, parsed)
  );
};

const clampInstructionsLimit = value => {
  const parsed = parseFiniteNumber(value);
  if (
    parsed === null ||
    parsed < MIN_ARROWS_AI_INSTRUCTIONS_LIMIT ||
    parsed > MAX_ARROWS_AI_INSTRUCTIONS_LIMIT
  ) {
    return DEFAULT_ARROWS_AI_INSTRUCTIONS_LIMIT;
  }

  return parsed;
};

const clampInboxesPerAssistant = value => {
  const parsed = parseFiniteNumber(value);
  if (
    parsed === null ||
    parsed < MIN_ARROWS_AI_INBOXES_PER_ASSISTANT ||
    parsed > MAX_ARROWS_AI_INBOXES_PER_ASSISTANT
  ) {
    return DEFAULT_ARROWS_AI_INBOXES_PER_ASSISTANT;
  }

  return parsed;
};

export function useArrowsAiAccount() {
  const { currentAccount } = useAccount();

  const settings = computed(() => currentAccount.value?.settings || {});

  return {
    provider: computed(() =>
      ARROWS_AI_PROVIDERS.includes(settings.value.arrows_ai_provider)
        ? settings.value.arrows_ai_provider
        : DEFAULT_ARROWS_AI_PROVIDER
    ),
    assistantLimit: computed(() =>
      clampLimit(settings.value.arrows_ai_assistants_limit)
    ),
    instructionsLimit: computed(() =>
      clampInstructionsLimit(settings.value.arrows_ai_instructions_limit)
    ),
    allowedChannelTypes: computed(() =>
      allowedChannelTypesFrom(settings.value.arrows_ai_channel_types)
    ),
    inboxesPerAssistant: computed(() =>
      clampInboxesPerAssistant(settings.value.arrows_ai_inboxes_per_assistant)
    ),
  };
}
